import React, { useMemo } from 'react';
import { Grid, makeStyles, useTheme, useMediaQuery } from '@material-ui/core';
import {
  CustomizedTypography,
  CustomizedDialog,
  CustomizedButton
} from '@components/Customized';
import useRequest from '@hooks/useRequest';
import { Assignment } from '@material-ui/icons';
import { CapaErrorCode, CapaQuotationServiceType } from '@generated/graphql';
import { RequestDetailForm } from '@components/Form';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import { clsx } from '@components/Provider';

interface RequestPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    e?: React.BaseSyntheticEvent<object, any, any> | undefined
  ) => Promise<void>;
}

const useStyles = makeStyles((theme) => {
  return {
    buttonRoot: {
      flexWrap: 'nowrap',
      gap: `12px`
    },
    securityRoot: {
      marginBottom: 32,
      [theme.breakpoints.down('xs')]: {
        marginBottom: 16
      }
    },
    contentRoot: {
      maxHeight: 450
    }
  };
});

const RequestPreviewDialog: React.FC<RequestPreviewDialogProps> = ({
  open,
  onSubmit,
  onClose
}) => {
  const matches = useMediaQuery(useTheme().breakpoints.down('xs'));
  const classes = useStyles();
  const { requestState } = useRequest();
  const createdAt = new Date().toString();
  const {
    rfqId,
    publicOpen,
    projectName,
    serviceType,
    detailedServiceType,
    detailedRequest,
    projectPurpose,
    projectPurposeDetail,
    dueDate,
    maxEstimatedBudget: maxBudget,
    uploadedDrawingFiles: drawingFiles,
    uploadedReferenceFiles: referenceFiles,
    budgetOpen,
    optionalServices,
    consultingStartMethod
  } = requestState;

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_Submit;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_Submit;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_Submit;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_Submit;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_Submit;
    }
  }, [serviceType]);

  return (
    <CustomizedDialog
      closeButton
      open={open}
      onClose={onClose}
      icon={<Assignment color="primary" />}
      title="요청내역 확인"
      maxWidth={760}
      gutter={0}
      fullWidth
      scroll="paper"
      actions={
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          className={clsx(classes.buttonRoot, 'noBorder')}
        >
          <CustomizedButton
            type="submit"
            size={matches ? 'medium' : 'large'}
            variant="contained"
            color="secondary"
            onClick={() => onClose()}
          >
            <i className="request-gtm-submitCancel-btn">돌아가서 수정하기</i>
          </CustomizedButton>
          <CustomizedButton
            logId={logId}
            type="submit"
            size={matches ? 'medium' : 'large'}
            color="primary"
            variant="contained"
            onClick={onSubmit}
          >
            <i className="request-gtm-submitComplete-btn">견적 요청하기</i>
          </CustomizedButton>
        </Grid>
      }
    >
      <Grid container direction="column">
        <CustomizedTypography
          color="textPrimary"
          variant={matches ? 'subtitle2' : 'body1'}
          fontWeight="500"
        >
          입력하신 요청내역을 확인해주세요!
        </CustomizedTypography>
        <CustomizedTypography
          color="textSecondary"
          variant={matches ? 'subtitle2' : 'body1'}
          fontWeight="500"
          gutterBottoms={16}
        >
          고객님의 견적 요청서는 파트너에게 다음과 같이 보여집니다.
        </CustomizedTypography>
      </Grid>

      <Grid container className={classes.contentRoot}>
        <RequestDetailForm
          isPreview={true}
          matches={matches}
          rfqInfo={{
            fetchQuotationUserRfqDetail: {
              errorMessage: null,
              result: CapaErrorCode.Success,
              data: {
                id: rfqId,
                publicOpen: publicOpen,
                projectName: projectName,
                serviceType: serviceType,
                detailedServiceType: detailedServiceType ?? undefined,
                projectPurpose: projectPurpose,
                projectPurposeDetail: projectPurposeDetail,
                dueDate: dueDate,
                createdAt: createdAt,
                maxEstimatedBudget: maxBudget,
                drawingFiles: drawingFiles as any,
                detailedRequest: detailedRequest!,
                referenceFiles: referenceFiles,
                budgetOpen: budgetOpen,
                optionalServices: optionalServices!,
                consultingStartMethod: consultingStartMethod
              }
            }
          }}
          onChangePath={() => {}}
        />
      </Grid>
    </CustomizedDialog>
  );
};

export default RequestPreviewDialog;
